/**
 * 1.7.0 stream B (docs/dev/formats-1.7.0-build.md): the Guides view's outlines over the primary picture: each other
 * format's crop window of the V1 clip under the playhead, coloured (by the format's place in the list) and labelled,
 * mapped through the primary's own crop; the parts outside the primary picture are dashed. Dragging an outline pans that
 * format, its corner handles zoom it (with keys: the key at the playhead, or a new one; without: the static framing),
 * and a click makes the format the active one. The active format's outline holds the graphics in that format's layout
 * (`activeGraphics`, "Graphics: 9:16").
 *
 * The outlines follow the picture on every frame of playback through the monitor's graphics clock, written to the
 * elements directly (no React render per frame).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { documentForFormat, type FormatRef, type ResolvedFormat, type SequenceDocument, type Size } from '@prism/contracts';
import type { EditorApply } from './formatContext';
import { formatColor } from './formatContext';
import { guideOutlines, type GuideOutline } from './monitor-guides';
import { insideFrame, panByOutlineDrag, panKeepingCentre, windowSize, zoomByCorner } from './monitor-geometry';
import { framingEdit } from './monitor-edit';

export interface FormatGuidesProps {
  doc: SequenceDocument;
  formats: ResolvedFormat[];
  active: FormatRef;
  /** Sequence ticks. */
  playhead: number;
  /** The primary picture's box on screen, in CSS pixels (what the outlines are drawn in). */
  stage: Size;
  /** Displayed picture sizes of the sources, by version id (for each clip's crop window). */
  sourceSizes: Map<string, Size>;
  /** Formats whose outline is shown, and the outlines' opacity (the overlays menu). */
  shown?: FormatRef[];
  opacity?: number;
  canEdit: boolean;
  apply: EditorApply;
  seal?: () => void;
  onActive: (id: FormatRef) => void;
  /** Added by stream B: the monitor's per-frame playhead listeners during playback (graphicsClock). */
  clock?: Set<(ph: number) => void>;
  /** Added by stream B: the graphics of the active format, drawn inside its outline. */
  activeGraphics?: ReactNode;
}

const pct = (v: number) => `${(v * 100).toFixed(4)}%`;

/** Write one outline's place (and the clip-path of its solid part, the part inside the primary picture). */
function place(el: HTMLElement, o: GuideOutline) {
  const r = o.rect;
  el.style.left = pct(r.x);
  el.style.top = pct(r.y);
  el.style.width = pct(r.w);
  el.style.height = pct(r.h);
  const inside = insideFrame(r);
  const solid = el.querySelector<HTMLElement>('.guide-solid');
  if (solid) {
    solid.style.clipPath = inside
      ? `inset(${pct((inside.y - r.y) / r.h)} ${pct((r.x + r.w - inside.x - inside.w) / r.w)} ${pct((r.y + r.h - inside.y - inside.h) / r.h)} ${pct((inside.x - r.x) / r.w)})`
      : 'inset(50%)';
  }
  el.dataset.outside = inside && inside.w >= r.w - 1e-4 && inside.h >= r.h - 1e-4 ? 'false' : 'true';
  el.dataset.editable = o.win && o.clipId ? 'true' : 'false';
}

export function FormatGuides({ doc, formats, active, playhead, stage, sourceSizes, shown, opacity = 1, canEdit, apply, seal, onActive, clock, activeGraphics }: FormatGuidesProps) {
  const docs = useMemo(() => new Map(formats.map((f) => [f.id, documentForFormat(doc, f.id)] as const)), [doc, formats]);
  const visible = formats.filter((f) => !f.isPrimary && (!shown || shown.includes(f.id) || f.id === active));
  const els = useRef(new Map<FormatRef, HTMLDivElement>());
  const last = useRef<GuideOutline[]>([]);
  const phRef = useRef(playhead);
  const layout = useCallback((ph: number) => {
    phRef.current = ph;
    const list = guideOutlines(docs, visible, ph, sourceSizes);
    last.current = list;
    for (const o of list) { const el = els.current.get(o.format.id); if (el) place(el, o); }
  }, [docs, visible.map((f) => f.id).join(','), sourceSizes]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { layout(playhead); }, [layout, playhead]);
  useEffect(() => {
    if (!clock) return;
    const onFrame = (ph: number) => layout(ph);
    clock.add(onFrame);
    return () => { clock.delete(onFrame); };
  }, [clock, layout]);

  const root = useRef<HTMLDivElement | null>(null);
  // sw/sh (1.9.0): the stage's size read from the DOM at pointer-down (the guides fill the picture), not the `stage`
  // prop, which can still be the unmeasured size when a fast drag starts right after a layout change.
  const drag = useRef<null | { id: FormatRef; o: GuideOutline; x0: number; y0: number; moved: boolean; corner: boolean; cx: number; cy: number; d0: number; sw: number; sh: number }>(null);
  const down = (f: ResolvedFormat, corner: boolean) => (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    // A corner's press bubbles to its outline: the corner has the drag already.
    if (!corner && drag.current?.corner) return;
    const o = last.current.find((x) => x.format.id === f.id);
    if (!o) return;
    const box = els.current.get(f.id)?.getBoundingClientRect();
    const cx = box ? box.left + box.width / 2 : e.clientX, cy = box ? box.top + box.height / 2 : e.clientY;
    const r = root.current?.getBoundingClientRect();
    const sw = r && r.width > 0 ? r.width : stage.width, sh = r && r.height > 0 ? r.height : stage.height;
    drag.current = { id: f.id, o, x0: e.clientX, y0: e.clientY, moved: false, corner, cx, cy, d0: Math.hypot(e.clientX - cx, e.clientY - cy), sw, sh };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 3) return;
    d.moved = true;
    const { o } = d;
    if (!canEdit || !o.win || !o.clipId || !(d.sw > 0) || !(d.sh > 0)) return;
    let next: { x: number; y: number; zoom: number };
    if (d.corner) {
      const zoom = zoomByCorner(o.zoom, d.d0, Math.hypot(e.clientX - d.cx, e.clientY - d.cy));
      const fa = o.format.width / o.format.height;
      const ws = windowSize(o.sourceAspect, fa, zoom);
      next = { ...panKeepingCentre(o.win, ws.w, ws.h), zoom };
    } else {
      next = { ...panByOutlineDrag(o.win, o.place, (e.clientX - d.x0) / d.sw, (e.clientY - d.y0) / d.sh), zoom: o.zoom };
    }
    const edit = framingEdit(doc, o.clipId, o.format.id, o.sourceTicks, next);
    if (edit) apply(edit.fn, edit.coalesce);
  };
  const up = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (!d.moved) onActive(d.id);
    else seal?.();
  };

  return (
    <div ref={root} className="format-guides" data-testid="format-guides" data-active={active} data-formats={formats.length} style={{ ['--guide-opacity' as string]: String(opacity) }}>
      {visible.map((f) => (
        <div key={f.id} ref={(el) => { if (el) els.current.set(f.id, el); else els.current.delete(f.id); }}
          className={`guide-outline ${f.id === active ? 'active' : ''} ${canEdit ? 'can-edit' : ''}`}
          // The active outline on top, so it can be dragged where outlines overlap (a Fit format's outline is the whole picture).
          style={{ ['--chip' as string]: formatColor(f.index), zIndex: f.id === active ? 3 : 1 }}
          data-testid={`guide-outline-${f.id}`} data-format={f.id}
          onPointerDown={down(f, false)} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
          <div className="guide-dash" aria-hidden />
          <div className="guide-solid" aria-hidden />
          {f.id === active && activeGraphics && <div className="guide-graphics">{activeGraphics}</div>}
          <span className="guide-label">{f.label}</span>
          {canEdit && (['tl', 'tr', 'bl', 'br'] as const).map((c) => (
            <span key={c} className={`guide-handle ${c}`} data-testid={`guide-handle-${f.id}-${c}`} aria-hidden
              onPointerDown={down(f, true)} />
          ))}
        </div>
      ))}
    </div>
  );
}
